import { useMemo, useState } from 'react';

import { ChakraBox, DropdownV2, InputGroupV2 } from '@equip.health/ui';

import { NoteEvents } from '~/analytics';
import ConfirmModal from '~/components/common/ConfirmModal';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGetNoteVoidReasonsEnumQuery } from '~/store/services/enumerations';
import Loading from '~/pages/loading';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const TOP_LEVEL_ID = 'void-note-modal';

interface VoidNoteModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onConfirm: (reason: NoteVoidReason) => void;
}

const VoidNoteModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
}: VoidNoteModalProps) => {
  const { trackEvent } = useAnalytics();
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const { data: voidReasons } = useGetNoteVoidReasonsEnumQuery();

  const menuOptions = useMemo(
    () =>
      (voidReasons ?? []).map((reason: NoteVoidReason) => ({
        label: reason.voidReason,
        value: reason.voidReasonExternalId,
      })),
    [voidReasons],
  );

  const handleTrackEvent = () => {
    trackEvent(NoteEvents.voidNoteModalNotesTabVoidNoteButtonClicked, {
      Source: AnalyticsSourceValues.NOTES_TAB,
    });
  };

  const handleOnClick = () => {
    handleTrackEvent();
    setIsOpen(false);
    onConfirm(
      voidReasons.find((n) => n.voidReasonExternalId === selectedReasonId),
    );
  };

  if (!voidReasons) {
    return <Loading />;
  }

  return (
    <ConfirmModal
      body="Are you sure you want to void this note? It will no longer be part of the patient's record."
      cancelLabel="Cancel"
      confirmLabel="Void note"
      confirmVariant="accent.critical"
      id={TOP_LEVEL_ID}
      isDisabled={!selectedReasonId}
      isOpen={isOpen}
      onClose={() => {
        trackEvent(NoteEvents.voidNoteModalNotesTabCancelButtonClicked, {
          Source: AnalyticsSourceValues.NOTES_TAB,
        });
        setIsOpen(false);
      }}
      onConfirm={handleOnClick}
      title="Void note"
    >
      <ChakraBox margin="12px 0px">
        <InputGroupV2
          id={`${TOP_LEVEL_ID}__void-reason-group`}
          isRequired
          label="Void reason"
          shouldShowAsteriskWhenRequired
        >
          <DropdownV2
            ariaLabel="Void reason"
            id={`${TOP_LEVEL_ID}__void-reason`}
            menuOptions={menuOptions}
            onOptionClick={(value) => setSelectedReasonId(value ?? null)}
            placeholder="Select one"
            value={selectedReasonId}
          />
        </InputGroupV2>
      </ChakraBox>
    </ConfirmModal>
  );
};

export default VoidNoteModal;
